import { ActiveTimerSnapshot, StudySession } from '../types/studySession';
import { getSyllabusUnitProgressWeight, normalizeSyllabusTitle } from '../constants/syllabusCatalog';
import { SyllabusUnit } from '../types/syllabus';

const REVISION_INTERVAL_DAYS = [1, 3, 7, 14, 30];
const localDayKey = (date: Date) => `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;

export function calculateSyllabusUnitProgress(
  units: readonly Pick<SyllabusUnit, 'title' | 'status'>[],
  subjectCode: string,
): { completed: number; total: number; percentage: number } {
  const totalWeight = units.reduce((sum, unit) => sum + getSyllabusUnitProgressWeight(subjectCode, unit.title), 0);
  const completedUnits = units.filter((unit) => unit.status === 'COMPLETED');
  const completedWeight = completedUnits.reduce((sum, unit) => sum + getSyllabusUnitProgressWeight(subjectCode, unit.title), 0);
  return {
    completed: completedUnits.length,
    total: units.length,
    percentage: totalWeight ? Math.round((completedWeight / totalWeight) * 100) : 0,
  };
}

export function getTimerRemainingSeconds(snapshot: ActiveTimerSnapshot, now = Date.now()): number {
  if (snapshot.startedAtTimestamp === null) return snapshot.targetSeconds;
  const activeMilliseconds = snapshot.status === 'PAUSED' && snapshot.pausedAtTimestamp !== null
    ? snapshot.pausedAtTimestamp - snapshot.startedAtTimestamp - snapshot.accumulatedPauseMs
    : now - snapshot.startedAtTimestamp - snapshot.accumulatedPauseMs;
  return Math.max(0, snapshot.targetSeconds - Math.floor(activeMilliseconds / 1000));
}

export function getNextRevisionDate(completedRevisionCount: number, from = new Date()): Date {
  const intervalIndex = Math.min(Math.max(0, completedRevisionCount), REVISION_INTERVAL_DAYS.length - 1);
  const nextDate = new Date(from);
  nextDate.setDate(nextDate.getDate() + REVISION_INTERVAL_DAYS[intervalIndex]);
  return nextDate;
}

export function getConsecutiveStudyDays(sessions: readonly StudySession[], now = new Date()): number {
  const studyDates = new Set(sessions.map((session) => localDayKey(new Date(session.startTime))));
  let count = 0;
  for (let offset = 0; offset < 7; offset++) {
    const date = new Date(now);
    date.setDate(date.getDate() - offset);
    if (!studyDates.has(localDayKey(date))) break;
    count++;
  }
  return count;
}

export function getHourlyStudySeconds(sessions: readonly StudySession[], localDate: string): number[] {
  const [year, month, day] = localDate.split('-').map(Number);
  const startOfDay = new Date(year, month - 1, day).getTime();
  const endOfDay = new Date(year, month - 1, day + 1).getTime();
  const buckets = Array.from({ length: 24 }, () => 0);

  for (const session of sessions) {
    const sessionStart = new Date(session.startTime).getTime();
    const sessionEnd = new Date(session.endTime).getTime();
    const sessionSpan = sessionEnd - sessionStart;
    if (sessionSpan <= 0 || sessionEnd <= startOfDay || sessionStart >= endOfDay) continue;
    const clippedStart = Math.max(sessionStart, startOfDay);
    const clippedEnd = Math.min(sessionEnd, endOfDay);
    for (let hour = 0; hour < 24; hour++) {
      const hourStart = new Date(year, month - 1, day, hour).getTime();
      const hourEnd = new Date(year, month - 1, day, hour + 1).getTime();
      const overlap = Math.max(0, Math.min(clippedEnd, hourEnd) - Math.max(clippedStart, hourStart));
      buckets[hour] += session.durationSeconds * overlap / sessionSpan;
    }
  }
  return buckets.map(Math.round);
}

export function findSyllabusUnitDuplicate(units: readonly SyllabusUnit[], candidate: Pick<SyllabusUnit, 'subjectId' | 'unitNumber' | 'title'>): 'title' | 'number' | null {
  const sameSubject = units.filter((unit) => unit.subjectId === candidate.subjectId);
  if (sameSubject.some((unit) => normalizeSyllabusTitle(unit.title) === normalizeSyllabusTitle(candidate.title))) return 'title';
  if (sameSubject.some((unit) => unit.unitNumber === candidate.unitNumber)) return 'number';
  return null;
}
